import { create } from 'zustand';

const LOGGED_OUT = { loggedIn: false, userRole: null, roles: [], user: null };

// Every role the account has; always includes the active one so older
// responses without `roles` still behave like a single-role account.
const normalizeRoles = (roles, activeRole) => {
  const list = Array.isArray(roles) ? roles.filter(Boolean) : [];
  if (activeRole && !list.includes(activeRole)) list.push(activeRole);
  return list;
};

const useStore = create((set, get) => ({
  ...LOGGED_OUT,
  socket: null,
  // One-shot header notice after a role switch ({ severity, text }); kept in
  // the store because the dashboard (and its Layout) remounts on switch.
  roleNotice: null,
  // Russian market first: default to 'ru', remember explicit choice
  language: localStorage.getItem('language') || 'ru',
  darkMode: localStorage.getItem('darkMode') === 'true',
  setLanguage: (language) => {
    localStorage.setItem('language', language);
    set({ language });
  },
  setDarkMode: (v) => {
    localStorage.setItem('darkMode', String(v));
    set({ darkMode: v });
  },
  setSocket: (socket) => set({ socket }),
  setRoleNotice: (roleNotice) => set({ roleNotice }),
  checkSession: async () => {
    try {
      const res = await fetch('/api/auth/me', { credentials: 'include' });
      const data = await res.json();
      if (data.loggedIn) {
        set({
          loggedIn: true,
          userRole: data.role,
          roles: normalizeRoles(data.roles, data.role),
          user: data.user,
        });
      } else {
        set(LOGGED_OUT);
      }
    } catch (error) {
      set(LOGGED_OUT);
    }
  },
  // Silent re-read of /me: never logs the user out on a failed request.
  refreshSession: async () => {
    try {
      const res = await fetch('/api/auth/me', { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      if (!data.loggedIn || !get().loggedIn) return;
      set((state) => ({
        userRole: data.role,
        roles: normalizeRoles(data.roles, data.role),
        user: { ...state.user, ...data.user },
      }));
    } catch (error) {
      // keep the current session state
    }
  },
  switchRole: async (role) => {
    const res = await fetch('/api/auth/active-role', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.message || 'Не удалось переключить режим');
    }
    set((state) => ({
      userRole: data.role,
      roles: normalizeRoles(data.roles, data.role),
      user: { ...state.user, ...data.user },
    }));
    return data.role;
  },
  login: (userRole, user, roles) => {
    set({
      loggedIn: true,
      userRole,
      user,
      roles: normalizeRoles(roles, userRole),
    });
    // The login response may not list every role; /me does.
    if (!Array.isArray(roles) && userRole !== 'parent') {
      get().refreshSession();
    }
  },
  logout: () => set({ ...LOGGED_OUT, roleNotice: null }),
}));

export default useStore;
